import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  CheckIcon,
  TrashIcon,
  CloudArrowUpIcon,
  PhotoIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button, IconButton, Badge, Modal, LoadingState, EmptyState } from './ui';

interface Imagen {
  nombre: string;
  tamano: number;
  fechaCreacion: string;
  url: string;
}

interface UsoImagen {
  en_uso: boolean;
  detalles: {
    tipo: string;
    analisis_id: number;
    analisis_nea: string;
  }[];
}

interface ImagenSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (nombreImagen: string) => void;
  tipo: 'firma' | 'logo'; // Tipo de imagen que se está seleccionando
}

const ImagenSelectorModal: React.FC<ImagenSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  tipo
}) => {
  const [imagenes, setImagenes] = useState<Imagen[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'seleccionar' | 'cargar'>('seleccionar');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imagenesEnUso, setImagenesEnUso] = useState<{[key: string]: boolean}>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imagenAEliminar, setImagenAEliminar] = useState<string | null>(null);
  const [mensajeError, setMensajeError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Cargar imágenes cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadImagenes();
      setSelectedImage(null); // Reset selection when modal opens
    }
  }, [isOpen]);

  const loadImagenes = async () => {
    try {
      setIsLoading(true);
      setMensajeError(null);

      const response = await api.get('/imagenes');
      setImagenes(response.data);

      // Verificar cuáles imágenes están en uso
      const usoMap: {[key: string]: boolean} = {};
      for (const img of response.data) {
        try {
          const usoResponse = await api.get(`/imagenes/verificar-uso/${img.nombre}`);
          usoMap[img.nombre] = usoResponse.data.en_uso;
        } catch (error) {
          console.error(`Error al verificar uso de imagen ${img.nombre}:`, error);
          usoMap[img.nombre] = true; // Por seguridad asumimos que está en uso
        }
      }
      setImagenesEnUso(usoMap);

    } catch (error) {
      console.error('Error al cargar imágenes:', error);
      setMensajeError('Error al cargar las imágenes. Por favor, recargue la página o intente más tarde.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        setUploadStatus('Error: Solo se permiten archivos de imagen');
        return;
      }

      // Validar tamaño máximo (2MB)
      if (file.size > 2 * 1024 * 1024) {
        setUploadStatus('Error: El archivo excede el límite de 2MB');
        return;
      }

      setSelectedFile(file);
      setUploadStatus(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('Por favor, seleccione una imagen');
      return;
    }

    try {
      setIsLoading(true);
      setUploadStatus('Subiendo imagen...');

      const formData = new FormData();
      formData.append('imagen', selectedFile);

      const response = await api.post('/imagenes', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadStatus('Imagen subida correctamente');
      setSelectedFile(null);

      // Recargar la lista de imágenes
      await loadImagenes();

      // Seleccionar automáticamente la imagen recién subida
      setSelectedImage(response.data.nombre);

      // Cambiar a la pestaña de seleccionar
      setActiveTab('seleccionar');
    } catch (error) {
      console.error('Error al subir imagen:', error);
      setUploadStatus('Error al subir la imagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectImage = () => {
    if (selectedImage) {
      onSelect(selectedImage);
      onClose();
    } else {
      alert('Por favor, seleccione una imagen');
    }
  };

  const handleDeleteClick = async (imagenNombre: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que se seleccione la imagen al hacer clic en eliminar

    // Verificar si la imagen está en uso
    try {
      const response = await api.get(`/imagenes/verificar-uso/${imagenNombre}`);
      const data = response.data as UsoImagen;

      if (data.en_uso) {
        // Mostrar mensaje de que no se puede eliminar porque está en uso
        const detalles = data.detalles.map(d => `${d.tipo === 'firma' ? 'Firma' : 'Logo'} en análisis ${d.analisis_nea}`).join(', ');
        alert(`No se puede eliminar la imagen porque está en uso: ${detalles}`);
        return;
      }

      // Si no está en uso, mostrar confirmación
      setImagenAEliminar(imagenNombre);
      setShowDeleteConfirm(true);
    } catch (error) {
      console.error(`Error al verificar si la imagen ${imagenNombre} está en uso:`, error);
      alert('Error al verificar si la imagen puede ser eliminada. Inténtelo de nuevo más tarde.');
    }
  };

  const confirmDelete = async () => {
    if (!imagenAEliminar) return;

    try {
      setIsDeleting(true);
      await api.delete(`/imagenes/${imagenAEliminar}`);

      // Eliminar la imagen de la lista
      setImagenes(prev => prev.filter(img => img.nombre !== imagenAEliminar));

      // Si la imagen eliminada era la seleccionada, deseleccionar
      if (selectedImage === imagenAEliminar) {
        setSelectedImage(null);
      }

      setShowDeleteConfirm(false);
      setImagenAEliminar(null);
    } catch (error: any) {
      console.error('Error al eliminar imagen:', error);
      if (error.response && error.response.data && error.response.data.mensaje) {
        alert(`Error: ${error.response.data.mensaje}`);
      } else {
        alert('Error al eliminar la imagen. Inténtelo de nuevo más tarde.');
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Si el modal no está abierto, no renderizar nada
  if (!isOpen) return null;

  return (
    <>
      <Modal title={tipo === 'firma' ? 'Seleccionar Firma' : 'Seleccionar Logo'} onClose={onClose} size="xl">
        {/* Tabs */}
        <div className="border-b border-slate-200 -mt-4 mb-4">
          <div className="flex gap-4">
            <button
              type="button"
              className={`px-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'seleccionar'
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('seleccionar')}
            >
              Seleccionar Imagen
            </button>
            <button
              type="button"
              className={`px-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'cargar'
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('cargar')}
            >
              Cargar Nueva Imagen
            </button>
          </div>
        </div>

        {mensajeError && (
          <div className="flex items-start gap-2 bg-red-50 border-l-4 border-red-400 text-red-700 text-sm p-4 mb-4 rounded-r-md" role="alert">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <p>{mensajeError}</p>
          </div>
        )}

        {/* Contenido de la pestaña "Seleccionar Imagen" */}
        {activeTab === 'seleccionar' && (
          <div className="flex flex-col" style={{ minHeight: '20rem' }}>
            {isLoading ? (
              <LoadingState label="Cargando imágenes..." />
            ) : imagenes.length > 0 ? (
              <div className="flex flex-col">
                <div className="max-h-[50vh] overflow-y-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 p-2">
                    {imagenes.map((imagen) => (
                      <div
                        key={imagen.nombre}
                        className={`relative border-2 rounded-lg p-3 cursor-pointer transition-all duration-200 hover:border-brand-400 hover:shadow-md ${
                          selectedImage === imagen.nombre
                            ? 'border-brand-500 ring-2 ring-brand-200 bg-brand-50'
                            : 'border-slate-200 bg-white'
                        }`}
                        onClick={() => setSelectedImage(imagen.nombre)}
                      >
                        <div className="w-full aspect-square relative mb-2 rounded overflow-hidden bg-slate-50">
                          <img
                            src={`${api.defaults.baseURL}${imagen.url}`}
                            alt={imagen.nombre}
                            className="absolute inset-0 w-full h-full object-contain"
                            onError={(e) => {
                              // Sin imagen de reemplazo externa: se oculta y queda
                              // el nombre del archivo debajo como referencia.
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          {selectedImage === imagen.nombre && (
                            <div className="absolute top-1 right-1 bg-brand-600 text-white rounded-full p-1">
                              <CheckIcon className="h-3 w-3" />
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <p className="text-xs text-slate-600 truncate flex-1 pr-2" title={imagen.nombre}>
                              {imagen.nombre}
                            </p>
                            {!imagenesEnUso[imagen.nombre] && (
                              <IconButton
                                label="Eliminar imagen"
                                tone="danger"
                                icon={<TrashIcon className="h-4 w-4" />}
                                onClick={(e) => handleDeleteClick(imagen.nombre, e)}
                                className="h-6 w-6"
                              />
                            )}
                          </div>
                          <Badge tone={imagenesEnUso[imagen.nombre] ? 'warning' : 'neutral'}>
                            {imagenesEnUso[imagen.nombre] ? 'En uso' : 'Disponible'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end">
                  <Button onClick={handleSelectImage} disabled={!selectedImage}>
                    Seleccionar
                  </Button>
                </div>
              </div>
            ) : (
              <EmptyState
                title="No hay imágenes disponibles"
                icon={<PhotoIcon className="h-6 w-6" />}
                action={
                  <Button variant="secondary" onClick={() => setActiveTab('cargar')}>
                    Cargar una nueva imagen
                  </Button>
                }
              />
            )}
          </div>
        )}

        {/* Contenido de la pestaña "Cargar Nueva Imagen" */}
        {activeTab === 'cargar' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-300 rounded-md p-6 text-center">
              {selectedFile ? (
                <div className="text-center">
                  <div className="w-32 h-32 mx-auto relative">
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Vista previa"
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  </div>
                  <p className="mt-2 text-sm font-medium text-green-600">{selectedFile.name}</p>
                  <p className="text-xs text-slate-500">{Math.round(selectedFile.size / 1024)} KB</p>
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    className="mt-2 px-3 py-1 text-xs text-red-600 hover:text-red-800 transition-colors"
                  >
                    Eliminar
                  </button>
                </div>
              ) : (
                <div>
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-slate-400" />
                  <p className="mt-2 text-sm text-slate-600">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-brand-700 hover:text-brand-800 transition-colors">
                      <span>Subir una imagen</span>
                      <input id="file-upload" name="file-upload" type="file" accept="image/*" className="sr-only" onChange={handleFileChange} />
                    </label>
                  </p>
                  <p className="text-xs text-slate-500">PNG, JPG, GIF hasta 2MB</p>
                </div>
              )}
            </div>

            {uploadStatus && (
              <div className={`text-sm ${
                uploadStatus.startsWith('Error')
                  ? 'text-red-600'
                  : uploadStatus === 'Imagen subida correctamente'
                    ? 'text-green-600'
                    : 'text-slate-600'
              }`}>
                {uploadStatus}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleUpload}
                disabled={!selectedFile}
                loading={isLoading}
                icon={<CloudArrowUpIcon className="h-4 w-4" />}
              >
                {isLoading ? 'Subiendo...' : 'Subir Imagen'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && (
        <Modal
          title="Confirmar eliminación"
          onClose={() => { setShowDeleteConfirm(false); setImagenAEliminar(null); }}
          size="sm"
          footer={
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                disabled={isDeleting}
                onClick={() => { setShowDeleteConfirm(false); setImagenAEliminar(null); }}
              >
                Cancelar
              </Button>
              <Button variant="danger" loading={isDeleting} onClick={confirmDelete}>
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          }
        >
          <p className="text-sm text-slate-600">
            ¿Está seguro que desea eliminar esta imagen? Esta acción no se puede deshacer.
          </p>
        </Modal>
      )}
    </>
  );
};

export default ImagenSelectorModal;
